import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Pill,
  Plus,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  Loader2,
  TrendingUp,
} from 'lucide-react'
import { useAgentStore } from '../store/useAgentStore'
import { fetcher, poster } from '../api/client'

interface Medication {
  id: number
  elderly_id: number
  name: string
  dosage: string
  frequency: string
  schedule_times: string[]
  instructions: string | null
  stock_count: number | null
  reorder_threshold: number
  is_active: boolean
  next_dose: string | null
  stock_status: 'ok' | 'low' | 'critical' | 'unknown'
}

interface Dose {
  medication_id: number
  name: string
  dosage: string
  time: string
  status: string
  instructions: string | null
}

interface Adherence {
  total_logged: number
  taken: number
  missed: number
  skipped: number
  delayed: number
  adherence_rate: number | null
}

const FREQUENCIES = [
  'once_daily',
  'twice_daily',
  'three_times_daily',
  'four_times_daily',
  'weekly',
  'as_needed',
] as const

const emptyForm = {
  name: '',
  dosage: '',
  frequency: 'once_daily',
  times: '08:00',
  instructions: '',
  stock_count: '',
}

export default function MedicationsPanel() {
  const { t } = useTranslation()
  const { selectedElderlyId } = useAgentStore()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(emptyForm)

  /* ───────── Queries ───────── */
  const { data: meds, isLoading } = useQuery<Medication[]>({
    queryKey: ['medications', selectedElderlyId],
    queryFn: () => fetcher<Medication[]>(`/medications/${selectedElderlyId}`),
    enabled: !!selectedElderlyId,
  })

  const { data: today } = useQuery<{ doses: Dose[] }>({
    queryKey: ['medications-today', selectedElderlyId],
    queryFn: () => fetcher<{ doses: Dose[] }>(`/medications/${selectedElderlyId}/today`),
    enabled: !!selectedElderlyId,
    refetchInterval: 60000,
  })

  const { data: adherence } = useQuery<Adherence>({
    queryKey: ['medications-adherence', selectedElderlyId],
    queryFn: () => fetcher<Adherence>(`/medications/${selectedElderlyId}/adherence`),
    enabled: !!selectedElderlyId,
  })

  /* ───────── Mutations ───────── */
  const addMedication = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      poster(`/medications/${selectedElderlyId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medications'] })
      queryClient.invalidateQueries({ queryKey: ['medications-today'] })
      setForm(emptyForm)
      setShowForm(false)
    },
  })

  const logIntake = useMutation({
    mutationFn: (medicationId: number) =>
      poster(`/medications/detail/${medicationId}/log?log_status=taken`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medications-today'] })
      queryClient.invalidateQueries({ queryKey: ['medications'] })
    },
  })

  const deleteMedication = useMutation({
    mutationFn: (medicationId: number) =>
      fetch(`/api/medications/detail/${medicationId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medications'] })
      queryClient.invalidateQueries({ queryKey: ['medications-today'] })
    },
  })

  /* ───────── Helpers ───────── */
  const statusIcon = (status: string) => {
    switch (status) {
      case 'taken':
        return <CheckCircle size={22} className="text-success-500" />
      case 'missed':
        return <XCircle size={22} className="text-danger-500" />
      case 'due':
        return <Clock size={22} className="text-warning-500" />
      case 'skipped':
      case 'delayed':
        return <AlertTriangle size={22} className="text-warning-600" />
      default:
        return <Clock size={22} className="text-gray-400" />
    }
  }

  const stockBadge = (status: Medication['stock_status']) => {
    switch (status) {
      case 'ok':
        return 'bg-success-50 text-success-700'
      case 'low':
        return 'bg-warning-50 text-warning-700'
      case 'critical':
        return 'bg-danger-50 text-danger-700'
      default:
        return 'bg-gray-100 text-gray-500'
    }
  }

  const handleSubmit = () => {
    if (!form.name.trim() || !form.dosage.trim()) return
    addMedication.mutate({
      name: form.name.trim(),
      dosage: form.dosage.trim(),
      frequency: form.frequency,
      schedule_times: form.times
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      instructions: form.instructions.trim() || null,
      stock_count: form.stock_count ? parseInt(form.stock_count) : null,
    })
  }

  /* ───────── Render Guards ───────── */
  if (!selectedElderlyId) {
    return (
      <div className="text-center py-20 text-gray-500 dark:text-gray-400 text-xl">
        {t('medications.no_elderly_selected')}
      </div>
    )
  }

  const doses = today?.doses || []
  const nextDose = doses.find((d) => d.status === 'due' || d.status === 'upcoming')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <Pill size={32} className="text-primary-600" />
            {t('medications.title')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">{t('medications.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn-primary flex items-center gap-2"
          disabled={addMedication.isPending}
        >
          {addMedication.isPending ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            <Plus size={20} />
          )}
          {t('medications.add')}
        </button>
      </div>

      {/* Adherence Summary */}
      {adherence && adherence.total_logged > 0 && (
        <div className="card flex items-center gap-6">
          <div className="p-4 rounded-2xl bg-primary-50">
            <TrendingUp size={32} className="text-primary-600" />
          </div>
          <div className="flex-1">
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('medications.adherence_rate')}
            </p>
            <div className="mt-2 h-4 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  (adherence.adherence_rate ?? 0) >= 80
                    ? 'bg-success-500'
                    : (adherence.adherence_rate ?? 0) >= 50
                      ? 'bg-warning-500'
                      : 'bg-danger-500'
                }`}
                style={{ width: `${adherence.adherence_rate ?? 0}%` }}
              />
            </div>
          </div>
          <div className="text-4xl font-bold text-gray-900 dark:text-white">
            {adherence.adherence_rate ?? '—'}%
          </div>
        </div>
      )}

      {/* Add Medication Form */}
      {showForm && (
        <div className="card space-y-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            {t('medications.add_title')}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">{t('medications.name_label')}</label>
              <input
                className="input-field"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t('medications.name_placeholder')}
              />
            </div>
            <div>
              <label className="label">{t('medications.dosage_label')}</label>
              <input
                className="input-field"
                value={form.dosage}
                onChange={(e) => setForm({ ...form, dosage: e.target.value })}
                placeholder="10mg"
              />
            </div>
            <div>
              <label className="label">{t('medications.frequency_label')}</label>
              <select
                className="input-field"
                value={form.frequency}
                onChange={(e) => setForm({ ...form, frequency: e.target.value })}
              >
                {FREQUENCIES.map((f) => (
                  <option key={f} value={f}>
                    {t(`medications.freq_${f}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">{t('medications.times_label')}</label>
              <input
                className="input-field"
                value={form.times}
                onChange={(e) => setForm({ ...form, times: e.target.value })}
                placeholder="08:00, 20:00"
              />
            </div>
            <div>
              <label className="label">{t('medications.stock_label')}</label>
              <input
                className="input-field"
                type="number"
                min="0"
                value={form.stock_count}
                onChange={(e) => setForm({ ...form, stock_count: e.target.value })}
                placeholder="30"
              />
            </div>
            <div>
              <label className="label">{t('medications.instructions_label')}</label>
              <input
                className="input-field"
                value={form.instructions}
                onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                placeholder={t('medications.instructions_placeholder')}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button className="btn-primary bg-gray-400 hover:bg-gray-500" onClick={() => setShowForm(false)}>
              {t('common.cancel')}
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={addMedication.isPending || !form.name.trim() || !form.dosage.trim()}
            >
              {t('medications.save')}
            </button>
          </div>
        </div>
      )}

      {/* Today's Schedule */}
      <div className="card">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Clock size={24} className="text-primary-600" />
          {t('medications.today_schedule')}
        </h3>

        {isLoading ? (
          <div className="text-center py-8 text-xl">{t('common.loading')}</div>
        ) : doses.length === 0 ? (
          <p className="text-center py-8 text-gray-500 dark:text-gray-400">
            {t('medications.no_meds_yet')}
          </p>
        ) : (
          <div className="space-y-3">
            {doses.map((dose, i) => (
              <div
                key={`${dose.medication_id}-${dose.time}-${i}`}
                className={`flex items-center gap-4 p-4 rounded-xl border ${
                  dose === nextDose
                    ? 'border-primary-300 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-100 dark:border-gray-700'
                }`}
              >
                {statusIcon(dose.status)}
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-bold text-gray-900 dark:text-white truncate">
                    {dose.name} · {dose.dosage}
                  </p>
                  {dose.instructions && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                      {dose.instructions}
                    </p>
                  )}
                </div>
                <span className="text-2xl font-bold text-gray-700 dark:text-gray-200 tabular-nums">
                  {dose.time}
                </span>
                {dose.status !== 'taken' && (
                  <button
                    onClick={() => logIntake.mutate(dose.medication_id)}
                    disabled={logIntake.isPending}
                    className="btn-primary !px-4 !py-2 !text-base flex items-center gap-2"
                  >
                    {logIntake.isPending ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <CheckCircle size={18} />
                    )}
                    {t('medications.mark_taken')}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Medication List */}
      <div className="card">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
          {t('medications.all_meds')}
        </h3>

        {!meds || meds.length === 0 ? (
          <p className="text-center py-8 text-gray-500 dark:text-gray-400">
            {t('medications.no_meds_yet')}
          </p>
        ) : (
          <div className="space-y-3">
            {meds.map((med) => (
              <div
                key={med.id}
                className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 dark:border-gray-700"
              >
                <div className="p-3 rounded-xl bg-primary-50">
                  <Pill size={24} className="text-primary-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-bold text-gray-900 dark:text-white truncate">
                    {med.name}{' '}
                    {!med.is_active && (
                      <span className="text-sm text-gray-400">({t('medications.inactive')})</span>
                    )}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {med.dosage} · {t(`medications.freq_${med.frequency}`)} ·{' '}
                    {med.schedule_times.join(' / ')}
                  </p>
                </div>
                <span
                  className={`px-3 py-1 rounded-lg text-sm font-bold ${stockBadge(med.stock_status)}`}
                >
                  {med.stock_count !== null
                    ? t(`medications.stock_${med.stock_status}`, { count: med.stock_count })
                    : t('medications.stock_unknown')}
                </span>
                <button
                  onClick={() => deleteMedication.mutate(med.id)}
                  disabled={deleteMedication.isPending}
                  className="p-3 rounded-xl text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                  aria-label={t('medications.delete')}
                >
                  <Trash2 size={22} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
